import { getAuthToken, API_BASE_URL } from './base';

export const getOrganizations = async () => {
  try {
    const token = await getAuthToken();
    const response = await fetch(`${API_BASE_URL}/api/organizations`, {
      method: "GET",
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!response.ok) throw new Error("Failed to fetch organizations");
    return await response.json();
  } catch (error) {
    console.error("Error fetching organizations:", error);
    throw error;
  }
};

export const createOrganization = async (name) => {
  try {
    const token = await getAuthToken();
    const response = await fetch(`${API_BASE_URL}/api/organizations`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ name }),
    });
    if (!response.ok) {
      const err = await response.json();
      throw new Error(err.error || "Failed to create organization");
    }
    return await response.json();
  } catch (error) {
    console.error("Error creating organization:", error);
    throw error;
  }
};

export const getOrganizationDetails = async (orgId) => {
  try {
    const token = await getAuthToken();
    const response = await fetch(`${API_BASE_URL}/api/organizations/${orgId}`, {
      method: "GET",
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!response.ok) throw new Error("Failed to fetch organization details");
    return await response.json();
  } catch (error) {
    console.error("Error fetching organization details:", error);
    throw error;
  }
};

export const updateOrganization = async (orgId, data) => {
  try {
    const token = await getAuthToken();
    const response = await fetch(`${API_BASE_URL}/api/organizations/${orgId}`, {
      method: "PUT",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      const err = await response.json();
      throw new Error(err.error || "Failed to update organization");
    }
    return await response.json();
  } catch (error) {
    console.error("Error updating organization:", error);
    throw error;
  }
};

export const getOrgMembers = async (orgId) => {
  try {
    const token = await getAuthToken();
    const response = await fetch(`${API_BASE_URL}/api/organizations/${orgId}/members`, {
      method: "GET",
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!response.ok) throw new Error("Failed to fetch members");
    return await response.json();
  } catch (error) {
    console.error("Error fetching organization members:", error);
    throw error;
  }
};

export const inviteMember = async (orgId, email, role) => {
  try {
    const token = await getAuthToken();
    const response = await fetch(`${API_BASE_URL}/api/organizations/${orgId}/members/invite`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ email, role }),
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.error || "Failed to invite member");
    return data;
  } catch (error) {
    console.error("Error inviting member:", error);
    throw error;
  }
};

export const updateMemberRole = async (orgId, memberId, role) => {
  try {
    const token = await getAuthToken();
    const response = await fetch(`${API_BASE_URL}/api/organizations/${orgId}/members/${memberId}`, {
      method: "PUT",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ role }),
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.error || "Failed to update member role");
    return data;
  } catch (error) {
    console.error("Error updating member role:", error);
    throw error;
  }
};

export const removeMember = async (orgId, memberId) => {
  try {
    const token = await getAuthToken();
    const response = await fetch(`${API_BASE_URL}/api/organizations/${orgId}/members/${memberId}`, {
      method: "DELETE",
      headers: { Authorization: `Bearer ${token}` },
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.error || "Failed to remove member");
    return data;
  } catch (error) {
    console.error("Error removing member:", error);
    throw error;
  }
};

export const revokeInvitation = async (orgId, invitationId) => {
  try {
    const token = await getAuthToken();
    const response = await fetch(`${API_BASE_URL}/api/organizations/${orgId}/invitations/${invitationId}`, {
      method: "DELETE",
      headers: { Authorization: `Bearer ${token}` },
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.error || "Failed to revoke invitation");
    return data;
  } catch (error) {
    console.error("Error revoking invitation:", error);
    throw error;
  }
};

export const resendInvitation = async (orgId, invitationId) => {
  try {
    const token = await getAuthToken();
    const response = await fetch(`${API_BASE_URL}/api/organizations/${orgId}/invitations/${invitationId}/resend`, {
      method: "POST",
      headers: { Authorization: `Bearer ${token}` },
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.error || "Failed to resend invitation");
    return data;
  } catch (error) {
    console.error("Error resending invitation:", error);
    throw error;
  }
};

export const acceptInvitation = async (token) => {
  try {
    const authToken = await getAuthToken();
    const response = await fetch(`${API_BASE_URL}/api/organizations/invitations/${token}/accept`, {
      method: "POST",
      headers: { Authorization: `Bearer ${authToken}` },
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.error || "Failed to accept invitation");
    return data;
  } catch (error) {
    console.error("Error accepting invitation:", error);
    throw error;
  }
};

export const declineInvitation = async (token) => {
  try {
    const authToken = await getAuthToken();
    const response = await fetch(`${API_BASE_URL}/api/organizations/invitations/${token}/decline`, {
      method: "POST",
      headers: { Authorization: `Bearer ${authToken}` },
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.error || "Failed to decline invitation");
    return data;
  } catch (error) {
    console.error("Error declining invitation:", error);
    throw error;
  }
};

export const getPendingInvitations = async () => {
  try {
    const token = await getAuthToken();
    const response = await fetch(`${API_BASE_URL}/api/organizations/invitations/pending`, {
      method: "GET",
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!response.ok) throw new Error("Failed to fetch pending invitations");
    return await response.json();
  } catch (error) {
    console.error("Error fetching pending invitations:", error);
    throw error;
  }
};
